import { useEffect, useRef } from 'react';

import { useAuth } from '@/hooks/useAuth';

/**
 * WatchAccount component
 *
 * Monitors wallet connection and automatically triggers backend authentication.
 * When user connects wallet via AppKit modal:
 * 1. Fetches auth message from backend
 * 2. Prompts user to sign message
 * 3. Submits signature to backend to create user entry in MySQL
 *
 * Updated: Now respects session verification state to prevent duplicate auth attempts
 */
function WatchAccount() {
  const { address, isConnected, isAuthenticated, authenticate, isAuthenticating, isVerifyingSession } = useAuth();
  const hasAttemptedAuth = useRef(false);

  useEffect(() => {
    // Trigger authentication when wallet connects and user is not yet authenticated
    // BUT: Don't trigger if we're currently verifying an existing session
    if (
      isConnected &&
      address &&
      !isAuthenticated &&
      !isAuthenticating &&
      !isVerifyingSession &&  // NEW: Wait for session verification to complete
      !hasAttemptedAuth.current
    ) {
      hasAttemptedAuth.current = true;

      // Delay slightly to ensure wallet provider is ready
      setTimeout(() => {
        authenticate().catch((error) => {
          console.error('Auto-authentication failed:', error);
          hasAttemptedAuth.current = false; // Allow retry
        });
      }, 500);
    }

    // Reset attempt flag when wallet disconnects or user becomes authenticated
    if (!isConnected || isAuthenticated) {
      hasAttemptedAuth.current = false;
    }
  }, [isConnected, address, isAuthenticated, isAuthenticating, isVerifyingSession, authenticate]);

  // No UI needed - AppKit modal handles wallet connection
  return null;
}

export default WatchAccount;
